import type { Locale } from '@/context/LangContext'
import {
  listServices,
  getPricing,
  recommendPlan,
  listProjectTypes,
  listQuickApps,
  listDatabases,
  listDevOpsTools,
  searchFaq,
  resolvePage,
  pageUrl,
  contactInfo,
  type PricingService,
  type PlanRegion,
} from '@/lib/webmcp/tools'

export interface WebMcpInputSchema {
  type: 'object'
  properties: Record<string, Record<string, unknown>>
  required?: string[]
}

export interface WebMcpTool {
  name: string
  description: string
  inputSchema: WebMcpInputSchema
  annotations?: { readOnlyHint?: boolean }
  execute: (args: unknown) => Promise<unknown>
}

interface ModelContextLike {
  registerTool: (tool: WebMcpTool) => Promise<void> | void
  unregisterTool?: (name: string) => Promise<void> | void
}

function getModelContext(): ModelContextLike | null {
  if (typeof document === 'undefined') return null
  const doc = document as Document & { modelContext?: ModelContextLike }
  if (doc.modelContext) return doc.modelContext
  const nav = navigator as Navigator & { modelContext?: ModelContextLike; modelContextTesting?: ModelContextLike }
  if (nav.modelContext) return nav.modelContext
  return null
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function strParam(args: unknown, key: string): string | undefined {
  if (!isRecord(args)) return undefined
  const v = args[key]
  return typeof v === 'string' ? v : undefined
}

function numParam(args: unknown, key: string): number | undefined {
  if (!isRecord(args)) return undefined
  const v = args[key]
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined
}

function toPricingService(v: string | undefined): PricingService {
  if (v === 'gpu' || v === 'sentry' || v === 'streaming') return v
  return 'vps'
}

function toRegion(v: string | undefined): PlanRegion | undefined {
  if (v === 'iran' || v === 'europe' || v === 'us-asia') return v
  return undefined
}

export function buildTools(lang: Locale): WebMcpTool[] {
  return [
    {
      name: 'lexoya_list_services',
      description: `List Lexoya cloud services with page URLs in '${lang}'. Use first to discover VPS, GPU, PaaS, database, storage, QuickApps, DevOps, MCP and pricing pages.`,
      inputSchema: { type: 'object', properties: {} },
      annotations: { readOnlyHint: true },
      execute: async () => listServices(lang),
    },
    {
      name: 'lexoya_get_pricing',
      description: 'Get Lexoya plan pricing. VPS supports region filter iran/europe/us-asia. GPU/Sentry/streaming ignore region.',
      inputSchema: {
        type: 'object',
        properties: {
          service: { type: 'string', enum: ['vps', 'gpu', 'sentry', 'streaming'], description: 'Service family' },
          region: { type: 'string', enum: ['iran', 'europe', 'us-asia'], description: 'VPS region (optional)' },
        },
        required: ['service'],
      },
      annotations: { readOnlyHint: true },
      execute: async (args) => getPricing(toPricingService(strParam(args, 'service')), toRegion(strParam(args, 'region'))),
    },
    {
      name: 'lexoya_recommend_plan',
      description: 'Recommend up to 3 Lexoya plans for a use case. Prefer this over manual price math. Returns plan name, CPU, RAM, disk, price.',
      inputSchema: {
        type: 'object',
        properties: {
          service: { type: 'string', enum: ['vps', 'gpu', 'sentry', 'streaming'] },
          region: { type: 'string', enum: ['iran', 'europe', 'us-asia'] },
          min_ram_gb: { type: 'number', description: 'Minimum RAM in GB' },
        },
      },
      annotations: { readOnlyHint: true },
      execute: async (args) =>
        recommendPlan({
          service: toPricingService(strParam(args, 'service')),
          region: toRegion(strParam(args, 'region')),
          minRamGb: numParam(args, 'min_ram_gb'),
        }),
    },
    {
      name: 'lexoya_list_project_types',
      description: 'List PaaS frameworks Lexoya can deploy: React, NextJS, Laravel, Django, Golang, Docker and more. Filter with optional query.',
      inputSchema: {
        type: 'object',
        properties: { query: { type: 'string', description: 'Substring filter, e.g. laravel' } },
      },
      annotations: { readOnlyHint: true },
      execute: async (args) => listProjectTypes(strParam(args, 'query')),
    },
    {
      name: 'lexoya_list_quickapps',
      description: 'List one-click QuickApps: WordPress, n8n, GitLab, Keycloak and more. Filter with optional query.',
      inputSchema: {
        type: 'object',
        properties: { query: { type: 'string', description: 'Substring filter, e.g. wordpress' } },
      },
      annotations: { readOnlyHint: true },
      execute: async (args) => listQuickApps(strParam(args, 'query')),
    },
    {
      name: 'lexoya_list_databases',
      description: 'List managed databases: PostgreSQL, MySQL, Redis, MongoDB, MariaDB and more. Filter with optional query.',
      inputSchema: {
        type: 'object',
        properties: { query: { type: 'string', description: 'Substring filter, e.g. postgres' } },
      },
      annotations: { readOnlyHint: true },
      execute: async (args) => listDatabases(strParam(args, 'query')),
    },
    {
      name: 'lexoya_list_devops_tools',
      description: 'List DevOps tools Lexoya operates: Kubernetes, Terraform, Prometheus, Kafka and more. Filter with optional query.',
      inputSchema: {
        type: 'object',
        properties: { query: { type: 'string', description: 'Substring filter, e.g. kuber' } },
      },
      annotations: { readOnlyHint: true },
      execute: async (args) => listDevOpsTools(strParam(args, 'query')),
    },
    {
      name: 'lexoya_get_faq',
      description: 'Answer Lexoya questions (services, pricing, billing, MCP endpoint https://api.lexoya.com/v1/mcp). Returns short cited Q&A in the page language.',
      inputSchema: {
        type: 'object',
        properties: {
          query: { type: 'string', description: 'User question keywords' },
          topic: { type: 'string', description: 'Optional: overview, vps, gpu, paas, database, quickapps, devops, mcp, billing, contact' },
        },
        required: ['query'],
      },
      annotations: { readOnlyHint: true },
      execute: async (args) => searchFaq(strParam(args, 'query') ?? '', strParam(args, 'topic'), lang),
    },
    {
      name: 'lexoya_navigate',
      description: 'Navigate the visible page to a Lexoya service (vps-server, gpu, pricing, docs, contact...). Use for next-step handoff so the user sees it.',
      inputSchema: {
        type: 'object',
        properties: {
          page: { type: 'string', description: 'Service key, e.g. pricing, gpu, contact, mcp' },
        },
        required: ['page'],
      },
      execute: async (args) => {
        const key = resolvePage(strParam(args, 'page') ?? '')
        if (!key) return { ok: false, error: 'Unknown page. Call lexoya_list_services first.' }
        const url = pageUrl(key, lang)
        window.location.assign(url)
        return { ok: true, url }
      },
    },
    {
      name: 'lexoya_get_contact',
      description: 'Get Lexoya contact page URL and console login URL for signup, sales or support handoff.',
      inputSchema: { type: 'object', properties: {} },
      annotations: { readOnlyHint: true },
      execute: async () => contactInfo(lang),
    },
  ]
}

export async function registerWebMcpTools(lang: Locale): Promise<() => void> {
  const ctx = getModelContext()
  if (!ctx) return () => undefined
  const tools = buildTools(lang)
  const registered: string[] = []
  for (const tool of tools) {
    try {
      await ctx.registerTool(tool)
      registered.push(tool.name)
    } catch {
      // Experimental API variance across Chrome builds — never break the page.
    }
  }
  return () => {
    if (!ctx.unregisterTool) return
    for (const name of registered) {
      try {
        const out = ctx.unregisterTool(name)
        if (out instanceof Promise) out.catch(() => undefined)
      } catch {
        // ignore cleanup errors
      }
    }
  }
}
